/**
 * trove-app (chrome-extension://) must sign in through the service worker so the
 * session matches api.ts getToken. Protocol mirrors trove-extension popup
 * POPUP_SIGN_IN / POPUP_GOOGLE_SIGN_IN / POPUP_SIGN_UP — keep in sync with trove-extension/src/popup/utils/messaging.ts.
 */

export interface SwAuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

export type SwMfaHintSummary = {
  index: number;
  factorId: string;
  displayName?: string | null;
  phoneNumber?: string | null;
};

export type SwMfaPendingPayload = {
  email: string;
  hints: SwMfaHintSummary[];
};

/** Thrown when SW sign-in needs MFA before completing. */
export class SwMfaRequiredError extends Error {
  readonly code = 'auth/multi-factor-auth-required';
  readonly mfaPending: SwMfaPendingPayload;

  constructor(mfaPending: SwMfaPendingPayload) {
    super('Multi-factor authentication required.');
    this.name = 'SwMfaRequiredError';
    this.mfaPending = mfaPending;
  }
}

/** Duck-type check — avoids `instanceof` failures across duplicate bundles. */
export function isSwMfaRequiredError(error: unknown): error is SwMfaRequiredError {
  if (error instanceof SwMfaRequiredError) return true;
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as SwMfaRequiredError).name === 'SwMfaRequiredError' &&
    Array.isArray((error as SwMfaRequiredError).mfaPending?.hints)
  );
}

const RESPONSE_TIMEOUT_MS = 10_000;

type ChromeRuntimeLike = {
  sendMessage: (message: unknown) => void;
  onMessage: {
    addListener: (cb: (message: Record<string, unknown>) => void) => void;
    removeListener: (cb: (message: Record<string, unknown>) => void) => void;
  };
};

function getChromeRuntime(): ChromeRuntimeLike | undefined {
  if (typeof window === 'undefined') return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const runtime = (window as any).chrome?.runtime as ChromeRuntimeLike | undefined;
  if (!runtime || typeof runtime.sendMessage !== 'function' || !runtime.onMessage?.addListener) {
    return undefined;
  }
  return runtime;
}

const GOOGLE_SIGN_IN_TIMEOUT_MS = 120_000;

/**
 * Ask the service worker for its current Firebase user (`getUser`). The SW's
 * handler waits for `authStateReady()`, so a non-null answer is authoritative
 * even right after a service-worker cold start.
 *
 * Returns `{ ok }` = whether the SW actually answered. Callers must treat
 * `ok === false` (SW unreachable / timed out) as *transient* and NOT sign the
 * user out — otherwise a torn-down MV3 worker looks like a logout and bounces
 * the embedded app login -> home.
 */
export async function getSwAuthUser(
  timeoutMs = 4_000
): Promise<{ ok: boolean; user: SwAuthUser | null }> {
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage) return { ok: false, user: null };
  return new Promise((resolve) => {
    let settled = false;
    const settle = (v: { ok: boolean; user: SwAuthUser | null }) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    const timeoutId = window.setTimeout(() => settle({ ok: false, user: null }), timeoutMs);
    try {
      (
        runtime.sendMessage as unknown as (
          msg: unknown,
          cb: (response?: { user?: SwAuthUser | null }) => void
        ) => void
      )({ action: 'getUser' }, (response) => {
        window.clearTimeout(timeoutId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const lastError = (window as any).chrome?.runtime?.lastError as
          | { message?: string }
          | undefined;
        if (lastError || !response) {
          settle({ ok: false, user: null });
          return;
        }
        settle({ ok: true, user: response.user ?? null });
      });
    } catch {
      window.clearTimeout(timeoutId);
      settle({ ok: false, user: null });
    }
  });
}

/**
 * Google sign-in via background Firebase (POPUP_GOOGLE_SIGN_IN).
 * OAuth runs in the service worker so the session matches api.getToken.
 */
export async function signInWithGoogleViaSw(): Promise<SwAuthUser> {
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage || !runtime.onMessage?.addListener) {
    throw new Error('Chrome extension runtime is not available.');
  }

  return new Promise((resolve, reject) => {
    const timeoutHandle = { id: 0 as number };
    const messageListener = (message: {
      type?: string;
      success?: boolean;
      user?: SwAuthUser;
      error?: string;
      code?: string;
      mfaPending?: SwMfaPendingPayload;
    }) => {
      if (message.type === 'POPUP_GOOGLE_SIGN_IN_RESPONSE') {
        runtime.onMessage.removeListener(messageListener);
        window.clearTimeout(timeoutHandle.id);
        if (message.success && message.user) {
          resolve(message.user);
          return;
        }
        if (message.code === 'auth/multi-factor-auth-required' && message.mfaPending) {
          reject(new SwMfaRequiredError(message.mfaPending));
          return;
        }
        reject(new Error(message.error || 'Google sign in failed'));
      }
    };

    runtime.onMessage.addListener(messageListener);

    timeoutHandle.id = window.setTimeout(() => {
      runtime.onMessage.removeListener(messageListener);
      reject(new Error('Google sign in timeout'));
    }, GOOGLE_SIGN_IN_TIMEOUT_MS);

    try {
      runtime.sendMessage({
        action: 'POPUP_GOOGLE_SIGN_IN',
        data: {},
      });
    } catch (e) {
      window.clearTimeout(timeoutHandle.id);
      runtime.onMessage.removeListener(messageListener);
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

/**
 * Email/password sign-in via background Firebase (POPUP_SIGN_IN).
 */
export async function signInWithEmailAndPasswordViaSw(
  email: string,
  password: string
): Promise<SwAuthUser> {
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage || !runtime.onMessage?.addListener) {
    throw new Error('Chrome extension runtime is not available.');
  }

  return new Promise((resolve, reject) => {
    const timeoutHandle = { id: 0 as number };
    const messageListener = (message: {
      type?: string;
      success?: boolean;
      user?: SwAuthUser;
      error?: string;
      code?: string;
      mfaPending?: SwMfaPendingPayload;
    }) => {
      if (message.type === 'POPUP_SIGN_IN_RESPONSE') {
        runtime.onMessage.removeListener(messageListener);
        window.clearTimeout(timeoutHandle.id);
        if (message.success && message.user) {
          resolve(message.user);
          return;
        }
        if (message.code === 'auth/multi-factor-auth-required' && message.mfaPending) {
          reject(new SwMfaRequiredError(message.mfaPending));
          return;
        }
        reject(new Error(message.error || 'Sign in failed'));
      }
    };

    runtime.onMessage.addListener(messageListener);

    timeoutHandle.id = window.setTimeout(() => {
      runtime.onMessage.removeListener(messageListener);
      reject(new Error('Sign in timeout'));
    }, RESPONSE_TIMEOUT_MS);

    try {
      runtime.sendMessage({
        action: 'POPUP_SIGN_IN',
        data: { email, password },
      });
    } catch (e) {
      window.clearTimeout(timeoutHandle.id);
      runtime.onMessage.removeListener(messageListener);
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

/**
 * Refresh the SW's `fbAuth.currentUser` from the auth server (auth emulator in
 * dev / Identity Toolkit in prod) and force-mint a new ID token, so embedded
 * React pages can verify the user's `email_verified` claim has flipped after
 * the user clicks the verification link. The SW will also re-broadcast
 * AUTH_STATE_CHANGED on success so AuthContext re-fetches `/preference`.
 */
export async function reloadCurrentUserViaSw(): Promise<{
  success: boolean;
  emailVerified?: boolean;
  user?: SwAuthUser | null;
  error?: string;
}> {
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage) {
    return { success: false, error: 'Chrome extension runtime is not available.' };
  }
  return new Promise((resolve) => {
    let settled = false;
    const settle = (
      v: { success: boolean; emailVerified?: boolean; user?: SwAuthUser | null; error?: string }
    ) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };
    const timeoutId = window.setTimeout(() => {
      settle({ success: false, error: 'reloadCurrentUser timeout' });
    }, RESPONSE_TIMEOUT_MS);
    try {
      (runtime.sendMessage as unknown as (
        msg: unknown,
        cb: (response: {
          success?: boolean;
          emailVerified?: boolean;
          user?: SwAuthUser | null;
          error?: string;
        }) => void
      ) => void)(
        { action: 'reloadCurrentUser' },
        (response) => {
          window.clearTimeout(timeoutId);
          if (!response) {
            settle({ success: false, error: 'No response from service worker' });
            return;
          }
          settle({
            success: Boolean(response.success),
            emailVerified: response.emailVerified,
            user: response.user ?? null,
            error: response.error,
          });
        }
      );
    } catch (e) {
      window.clearTimeout(timeoutId);
      settle({ success: false, error: e instanceof Error ? e.message : String(e) });
    }
  });
}

/**
 * Account creation via background Firebase (POPUP_SIGN_UP).
 */
export async function completeMfaSignInViaSw(opts: {
  code: string;
  hintIndex?: number;
  phoneVerificationId?: string;
}): Promise<SwAuthUser> {
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage || !runtime.onMessage?.addListener) {
    throw new Error('Chrome extension runtime is not available.');
  }

  return new Promise((resolve, reject) => {
    const timeoutHandle = { id: 0 as number };
    const messageListener = (message: {
      type?: string;
      success?: boolean;
      user?: SwAuthUser;
      error?: string;
    }) => {
      if (message.type === 'POPUP_MFA_VERIFY_RESPONSE') {
        runtime.onMessage.removeListener(messageListener);
        window.clearTimeout(timeoutHandle.id);
        if (message.success && message.user) {
          resolve(message.user);
        } else {
          reject(new Error(message.error || 'MFA verification failed'));
        }
      }
    };

    runtime.onMessage.addListener(messageListener);
    timeoutHandle.id = window.setTimeout(() => {
      runtime.onMessage.removeListener(messageListener);
      reject(new Error('MFA verify timeout'));
    }, RESPONSE_TIMEOUT_MS);

    try {
      runtime.sendMessage({ action: 'POPUP_MFA_VERIFY', data: opts });
    } catch (e) {
      window.clearTimeout(timeoutHandle.id);
      runtime.onMessage.removeListener(messageListener);
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

export async function cancelMfaSignInViaSw(): Promise<void> {
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage) return;
  runtime.sendMessage({ action: 'POPUP_MFA_CANCEL', data: {} });
}

type MfaSendSmsResponse = {
  type?: string;
  success?: boolean;
  verificationId?: string;
  error?: string;
};

function parseMfaSendSmsResponse(message: MfaSendSmsResponse): string {
  if (message.success && message.verificationId) return message.verificationId;
  throw new Error(message.error || 'Failed to send SMS');
}

/** Request SMS MFA code via service worker (reCAPTCHA runs in extension popup). */
export async function sendMfaPhoneVerificationViaSw(hintIndex = 0): Promise<string> {
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage || !runtime.onMessage?.addListener) {
    throw new Error('Chrome extension runtime is not available.');
  }

  return new Promise((resolve, reject) => {
    const timeoutHandle = { id: 0 as number };
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutHandle.id);
      runtime.onMessage.removeListener(messageListener);
      fn();
    };

    const messageListener = (message: MfaSendSmsResponse) => {
      if (message.type !== 'POPUP_MFA_SEND_SMS_RESPONSE') return;
      finish(() => {
        try {
          resolve(parseMfaSendSmsResponse(message));
        } catch (e) {
          reject(e instanceof Error ? e : new Error(String(e)));
        }
      });
    };

    runtime.onMessage.addListener(messageListener);
    timeoutHandle.id = window.setTimeout(() => {
      finish(() => reject(new Error('Send SMS timeout')));
    }, 120_000);

    try {
      const sendMessageWithCallback = runtime.sendMessage as (
        msg: unknown,
        cb?: (response: MfaSendSmsResponse) => void
      ) => void;
      sendMessageWithCallback({ action: 'POPUP_MFA_SEND_SMS', data: { hintIndex } }, (response) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const lastError = (window as any).chrome?.runtime?.lastError as { message?: string } | undefined;
        if (lastError?.message) {
          finish(() => reject(new Error(lastError.message)));
          return;
        }
        if (response && typeof response === 'object' && 'success' in response) {
          finish(() => {
            try {
              resolve(parseMfaSendSmsResponse(response));
            } catch (e) {
              reject(e instanceof Error ? e : new Error(String(e)));
            }
          });
        }
      });
    } catch (e) {
      finish(() => reject(e instanceof Error ? e : new Error(String(e))));
    }
  });
}

export async function signUpViaSw(
  email: string,
  password: string,
  displayName?: string
): Promise<SwAuthUser> {
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage || !runtime.onMessage?.addListener) {
    throw new Error('Chrome extension runtime is not available.');
  }

  return new Promise((resolve, reject) => {
    const timeoutHandle = { id: 0 as number };
    const messageListener = (message: {
      type?: string;
      success?: boolean;
      user?: SwAuthUser;
      error?: string;
    }) => {
      if (message.type === 'POPUP_SIGN_UP_RESPONSE') {
        runtime.onMessage.removeListener(messageListener);
        window.clearTimeout(timeoutHandle.id);
        if (message.success && message.user) {
          resolve(message.user);
        } else {
          reject(new Error(message.error || 'Sign up failed'));
        }
      }
    };

    runtime.onMessage.addListener(messageListener);

    timeoutHandle.id = window.setTimeout(() => {
      runtime.onMessage.removeListener(messageListener);
      reject(new Error('Sign up timeout'));
    }, RESPONSE_TIMEOUT_MS);

    try {
      runtime.sendMessage({
        action: 'POPUP_SIGN_UP',
        data: { email, password, displayName },
      });
    } catch (e) {
      window.clearTimeout(timeoutHandle.id);
      runtime.onMessage.removeListener(messageListener);
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

/**
 * Update Firebase Auth display name via the service worker (extension app pages).
 */
export async function updateProfileViaSw(displayName: string): Promise<SwAuthUser> {
  const runtime = getChromeRuntime();
  if (!runtime?.sendMessage || !runtime.onMessage?.addListener) {
    throw new Error('Chrome extension runtime is not available.');
  }

  return new Promise((resolve, reject) => {
    const timeoutHandle = { id: 0 as number };
    const messageListener = (message: {
      type?: string;
      success?: boolean;
      user?: SwAuthUser;
      error?: string;
    }) => {
      if (message.type === 'POPUP_UPDATE_PROFILE_RESPONSE') {
        runtime.onMessage.removeListener(messageListener);
        window.clearTimeout(timeoutHandle.id);
        if (message.success && message.user) {
          resolve(message.user);
        } else {
          reject(new Error(message.error || 'Could not update profile'));
        }
      }
    };

    runtime.onMessage.addListener(messageListener);

    timeoutHandle.id = window.setTimeout(() => {
      runtime.onMessage.removeListener(messageListener);
      reject(new Error('Profile update timeout'));
    }, RESPONSE_TIMEOUT_MS);

    try {
      runtime.sendMessage({
        action: 'POPUP_UPDATE_PROFILE',
        data: { displayName },
      });
    } catch (e) {
      window.clearTimeout(timeoutHandle.id);
      runtime.onMessage.removeListener(messageListener);
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}
